import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  mapEngagementFeedToLegacy,
  mapEngagementSystemToLegacyFeed,
  usersToUserEntities,
} from '@epicuro-next/data/engagement/engagement-api-utils';
import {
  EngagementFeedsPagedResponse,
  EngagementFeedWithUser,
  ENGAGEMENT_API_TOKEN,
  NewsfeedAddRequestDto,
  NewsfeedGetRequestDto,
  NewsfeedResponseDto,
  NewsfeedResponseDtoPageResponseDto,
  NewsfeedUpdatePromoteReferenceDto,
  NewsfeedUpdateRequestDto,
} from '@epicuro-next/data/engagement/engagement-models';
import { UsersApiService } from '@epicuro-next/data/users';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable()
export class NewsfeedApiService {
  private resource: string;

  constructor(
    private http: HttpClient,
    private usersApiService: UsersApiService,
    @Inject(ENGAGEMENT_API_TOKEN) private readonly baseUrl: string,
  ) {
    this.resource = `${this.baseUrl}/newsfeed`;
  }

  public getNewsfeed(payload: NewsfeedGetRequestDto) {
    const params = toHttpParams(payload);

    return this.http.get<NewsfeedResponseDtoPageResponseDto>(this.resource, {
      params,
    });
  }

  public fetch(
    payload: NewsfeedGetRequestDto,
  ): Observable<EngagementFeedsPagedResponse> {
    return this.getNewsfeed(payload).pipe(
      switchMap((resp) => {
        if (!resp.items?.length) {
          return of(mapEngagementSystemToLegacyFeed(resp, []));
        }

        const externalIds = resp.items.map(
          (item) => item.referenceCreatedByUserId,
        );

        return this.usersApiService
          .getUsersByExternalId(externalIds)
          .pipe(
            map((users) =>
              mapEngagementSystemToLegacyFeed(resp, usersToUserEntities(users)),
            ),
          );
      }),
    );
  }

  public get(id: string): Observable<EngagementFeedWithUser> {
    const request$ = this.http.get<NewsfeedResponseDto>(
      `${this.resource}/${id}`,
    );

    return this.feedWithUser(request$);
  }

  public delete(id: string) {
    return this.http.delete<void>(`${this.resource}/${id}`);
  }

  public create(
    article: NewsfeedAddRequestDto,
  ): Observable<EngagementFeedWithUser> {
    const request$ = this.http.post<NewsfeedResponseDto>(
      this.resource,
      article,
    );
    return this.feedWithUser(request$);
  }

  public update(
    id: string,
    payload: NewsfeedUpdateRequestDto,
  ): Observable<EngagementFeedWithUser> {
    const request$ = this.http.put<NewsfeedResponseDto>(
      `${this.resource}/${id}`,
      payload,
    );
    return this.feedWithUser(request$);
  }

  public pin(id: string) {
    return this.http.patch<void>(`${this.resource}/${id}/pin`, {});
  }

  public unpin(id: string) {
    return this.http.patch<void>(`${this.resource}/${id}/unpin`, {});
  }

  public updatePromoteByReferences({
    id,
    promoteReference,
  }: NewsfeedUpdatePromoteReferenceDto) {
    return this.http.patch<void>(
      `${this.resource}/${id}/promotes`,
      promoteReference,
    );
  }

  public excludeFeed(id: string) {
    return this.http.patch<void>(`${this.resource}/${id}/exclude`, {});
  }

  private feedWithUser<T extends NewsfeedResponseDto>(request$: Observable<T>) {
    return request$.pipe(
      switchMap((resp) =>
        this.usersApiService
          .getUsersByExternalId([resp.referenceCreatedByUserId])
          .pipe(
            map((users) =>
              mapEngagementFeedToLegacy(resp, usersToUserEntities(users)),
            ),
          ),
      ),
    );
  }
}

function toHttpParams(params: NewsfeedGetRequestDto) {
  return Object.getOwnPropertyNames(params).reduce((httpParams, key) => {
    const value = (params as unknown as { [key: string]: unknown })[key];
    const transformValue =
      typeof value === 'string' ? encodeURI(value) : JSON.stringify(value);

    return httpParams.set(key, transformValue);
  }, new HttpParams());
}
